
// FIX: Import React to make React types available.
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { confirmPasswordReset } from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { auth } from "../src/firebase";
import { PasswordInput } from "../components/PasswordInput";
import { SpinnerIcon } from "../components/icons/SpinnerIcon";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [oobCode, setOobCode] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [isPasswordStrong, setIsPasswordStrong] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const code = searchParams.get("oobCode");
    if (code) {
      setOobCode(code);
    } else {
      setError("Invalid or missing password reset link. Please request a new one.");
    }
    window.dispatchEvent(new Event('app-rendered'));
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!oobCode) {
      setError("Cannot proceed without a valid reset code.");
      return;
    }
    if (!isPasswordStrong) {
      setError("Password is not strong enough.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccessMessage("✅ Password has been reset successfully! You can now log in with your new password.");
      setTimeout(() => navigate("/"), 4000); // Redirect to home, user can click login
    } catch (err) {
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case "auth/expired-action-code":
            setError("This password reset link has expired. Please request a new one.");
            break;
          case "auth/invalid-action-code":
            setError("This password reset link is invalid or has already been used.");
            break;
          case "auth/weak-password":
            setError("The new password is not strong enough.");
            break;
          default:
            setError("An unexpected error occurred. Please try again.");
        }
      } else {
        setError("An unknown error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white shadow-lg p-8 rounded-xl max-w-md w-full border border-gray-200">
        <h2 className="text-2xl font-bold text-center text-[#4F46E5]">
          Set a New Password
        </h2>

        {successMessage ? (
          <div className="mt-6 text-center">
            <p className="text-green-700">{successMessage}</p>
            <Link to="/" className="mt-4 inline-block text-[#4F46E5] hover:text-[#6366F1]">
              Go to Homepage
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 mt-8">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}
            
            <PasswordInput
              label="New Password"
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onStrengthChange={setIsPasswordStrong}
              showStrengthMeter
              required
              minLength={8}
              disabled={!oobCode || isLoading}
            />
            
            <PasswordInput
              label="Confirm New Password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={!oobCode || isLoading}
            />
            
            <button
              type="submit"
              disabled={!oobCode || isLoading}
              className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300 disabled:opacity-50"
            >
              {isLoading ? <><SpinnerIcon /> Resetting...</> : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
